import type { SVGProps } from "react"

// Vector artwork shared with Procurement; every fill uses semantic theme tokens.
type StatusIllustrationProps = Pick<SVGProps<SVGSVGElement>, "className">

export function ServiceInterruptionIllustration({
  className,
}: StatusIllustrationProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      focusable="false"
      viewBox="0 0 320 224"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="166" cy="112" r="100" fill="var(--muted)" opacity="0.6" />
      <ellipse cx="166" cy="202" rx="128" ry="7" fill="var(--muted)" />

      <path
        d="M178 132h18"
        fill="none"
        stroke="var(--muted-foreground)"
        strokeLinecap="round"
        strokeWidth="3"
      />
      <path d="M98 164h18l4 24H94z" fill="var(--muted-foreground)" opacity="0.5" />
      <rect
        x="79"
        y="188"
        width="56"
        height="6"
        rx="3"
        fill="var(--muted-foreground)"
        opacity="0.65"
      />
      <rect x="36" y="64" width="142" height="100" rx="10" fill="var(--primary)" />
      <rect x="43" y="71" width="128" height="72" rx="4" fill="var(--card)" />
      <path d="M43 75a4 4 0 0 1 4-4h120a4 4 0 0 1 4 4v7H43z" fill="var(--muted)" />
      <circle cx="50" cy="76.5" r="1.8" fill="var(--muted-foreground)" opacity="0.5" />
      <circle cx="56" cy="76.5" r="1.8" fill="var(--muted-foreground)" opacity="0.5" />
      <circle cx="62" cy="76.5" r="1.8" fill="var(--muted-foreground)" opacity="0.5" />
      <rect
        x="52"
        y="90"
        width="46"
        height="5"
        rx="2.5"
        fill="var(--muted-foreground)"
        opacity="0.35"
      />
      <rect
        x="52"
        y="100"
        width="72"
        height="5"
        rx="2.5"
        fill="var(--muted-foreground)"
        opacity="0.35"
      />
      <rect x="52" y="111" width="54" height="24" rx="3" fill="var(--muted)" />
      <rect x="112" y="111" width="50" height="24" rx="3" fill="var(--muted)" />
      <circle cx="107" cy="154" r="2.5" fill="var(--primary-foreground)" opacity="0.6" />

      <rect x="196" y="123" width="18" height="18" rx="4" fill="var(--primary)" />
      <rect x="214" y="126" width="8" height="3" rx="1.5" fill="var(--muted-foreground)" />
      <rect x="214" y="135" width="8" height="3" rx="1.5" fill="var(--muted-foreground)" />

      <rect x="242" y="121" width="18" height="22" rx="4" fill="var(--primary)" />
      <rect x="245" y="126" width="4" height="3" rx="1" fill="var(--primary-foreground)" />
      <rect x="245" y="135" width="4" height="3" rx="1" fill="var(--primary-foreground)" />
      <path
        d="M260 132c16 0 4-26-6-44"
        fill="none"
        stroke="var(--muted-foreground)"
        strokeLinecap="round"
        strokeWidth="3"
      />

      <path
        d="M226 88a14 14 0 0 1 0-28a20 20 0 0 1 38-6a15 15 0 0 1 12 34z"
        fill="var(--card)"
        stroke="var(--border)"
        strokeLinejoin="round"
        strokeWidth="2"
      />
      <g
        fill="none"
        stroke="var(--warning)"
        strokeLinecap="round"
        strokeWidth="2.5"
      >
        <path d="M232 120v-6" />
        <path d="M232 150v-6" />
        <path d="M225 116l-3-4" />
        <path d="M239 116l3-4" />
      </g>

      {/* Warning badge sits inside the cloud: the remote service is the side that is unavailable. */}
      <g className="motion-safe:animate-pulse">
        <circle cx="249" cy="70" r="11" fill="var(--warning)" />
        <path
          d="M249 64v7"
          stroke="var(--warning-foreground)"
          strokeLinecap="round"
          strokeWidth="2.5"
        />
        <circle cx="249" cy="75.5" r="1.6" fill="var(--warning-foreground)" />
      </g>
    </svg>
  )
}

export function PageNotFoundIllustration({
  className,
}: StatusIllustrationProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      focusable="false"
      viewBox="0 0 320 224"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="160" cy="112" r="100" fill="var(--muted)" opacity="0.6" />
      <ellipse cx="160" cy="202" rx="128" ry="7" fill="var(--muted)" />

      <path
        d="M124 196c34 0 50-8 62-30s22-40 44-40"
        fill="none"
        stroke="var(--muted-foreground)"
        strokeDasharray="2 8"
        strokeLinecap="round"
        strokeWidth="3.5"
        opacity="0.5"
      />

      <ellipse cx="112" cy="198" rx="14" ry="3" fill="var(--muted-foreground)" opacity="0.3" />
      <rect x="108" y="62" width="8" height="136" rx="3" fill="var(--muted-foreground)" opacity="0.75" />
      <circle cx="112" cy="62" r="6" fill="var(--muted-foreground)" />
      <g transform="rotate(-4 112 86)">
        <path
          d="M72 74h72l12 12-12 12H72a4 4 0 0 1-4-4V78a4 4 0 0 1 4-4z"
          fill="var(--primary)"
        />
        <rect
          x="78"
          y="83"
          width="46"
          height="6"
          rx="3"
          fill="var(--primary-foreground)"
          opacity="0.75"
        />
      </g>
      <g transform="rotate(5 117 120)">
        <path
          d="M156 108H86l-12 12 12 12h70a4 4 0 0 0 4-4v-16a4 4 0 0 0-4-4z"
          fill="var(--card)"
          stroke="var(--border)"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <rect
          x="96"
          y="117"
          width="44"
          height="6"
          rx="3"
          fill="var(--muted-foreground)"
          opacity="0.4"
        />
      </g>
      <g transform="rotate(-3 113 152)">
        <path
          d="M84 142h52l10 10-10 10H84a4 4 0 0 1-4-4v-12a4 4 0 0 1 4-4z"
          fill="var(--muted)"
          stroke="var(--border)"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <rect
          x="90"
          y="149"
          width="30"
          height="6"
          rx="3"
          fill="var(--muted-foreground)"
          opacity="0.35"
        />
      </g>

      <ellipse cx="236" cy="124" rx="11" ry="3" fill="var(--muted-foreground)" opacity="0.3" />
      <path
        d="M236 120c-15-16-23-28-23-40a23 23 0 0 1 46 0c0 12-8 24-23 40z"
        fill="var(--primary)"
      />
      <circle cx="236" cy="80" r="13" fill="var(--card)" />
      <path
        d="M231.5 76a4.5 4.5 0 1 1 6.4 4.1c-1.4.7-1.9 1.7-1.9 3v.9"
        fill="none"
        stroke="var(--muted-foreground)"
        strokeLinecap="round"
        strokeWidth="2.8"
      />
      <circle cx="236" cy="88.5" r="1.7" fill="var(--muted-foreground)" />
    </svg>
  )
}

export function SignInRequiredIllustration({
  className,
}: StatusIllustrationProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      focusable="false"
      viewBox="0 0 320 224"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="160" cy="112" r="100" fill="var(--muted)" opacity="0.6" />
      <ellipse cx="160" cy="202" rx="128" ry="7" fill="var(--muted)" />

      <rect
        x="84"
        y="44"
        width="128"
        height="146"
        rx="12"
        fill="var(--card)"
        stroke="var(--border)"
        strokeWidth="2"
      />
      <path d="M84 56a12 12 0 0 1 12-12h104a12 12 0 0 1 12 12v6H84z" fill="var(--primary)" />
      <circle cx="148" cy="90" r="17" fill="var(--muted)" />
      <g fill="var(--muted-foreground)" opacity="0.6">
        <circle cx="148" cy="85" r="5.5" />
        <path d="M137 101a11 11 0 0 1 22 0z" />
      </g>
      <rect x="100" y="118" width="96" height="15" rx="4" fill="var(--muted)" />
      <rect
        x="107"
        y="123.5"
        width="42"
        height="4"
        rx="2"
        fill="var(--muted-foreground)"
        opacity="0.35"
      />
      <rect x="100" y="139" width="96" height="15" rx="4" fill="var(--muted)" />
      <g fill="var(--muted-foreground)" opacity="0.6">
        <circle cx="110" cy="146.5" r="2" />
        <circle cx="118" cy="146.5" r="2" />
        <circle cx="126" cy="146.5" r="2" />
        <circle cx="134" cy="146.5" r="2" />
        <circle cx="142" cy="146.5" r="2" />
      </g>
      <rect x="100" y="162" width="96" height="16" rx="4" fill="var(--primary)" />

      <g transform="rotate(-35 222 128)">
        <circle
          cx="222"
          cy="128"
          r="13"
          fill="var(--card)"
          stroke="var(--warning)"
          strokeWidth="7"
        />
        <rect x="235" y="125" width="42" height="6" rx="3" fill="var(--warning)" />
        <rect x="261" y="130" width="5" height="9" rx="1.5" fill="var(--warning)" />
        <rect x="270" y="130" width="5" height="7" rx="1.5" fill="var(--warning)" />
      </g>
    </svg>
  )
}
